import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { listProjects, createProject, type Project, type CreateProjectData, type UpdateProjectData } from '../../api/projects';
import { DataTable, type Column } from '../common/DataTable';
import { StatusBadge } from '../common/StatusBadge';
import { Modal } from '../common/Modal';
import { ProjectForm } from './ProjectForm';

export function ProjectsView() {
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listProjects(token);
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async (data: CreateProjectData | UpdateProjectData) => {
    const token = await getJwtToken();
    if (!token) return;
    await createProject(data as CreateProjectData, token);
    setShowAddModal(false);
    await fetchProjects();
  };

  const columns: Column<Project>[] = [
    { key: 'projectId', header: 'Project ID' },
    { key: 'name', header: 'Name' },
    { key: 'teamId', header: 'Team', render: (p) => p.teamId || '--' },
    {
      key: 'enabled',
      header: 'Status',
      render: (p) => <StatusBadge status={p.enabled ? 'Active' : 'Disabled'} />,
    },
    {
      key: 'maxBudget',
      header: 'Budget',
      render: (p) => {
        if (p.maxBudget == null) return <span style={{ color: 'var(--text-muted)' }}>No limit</span>;
        const spent = p.spend ?? 0;
        const pct = Math.min((spent / p.maxBudget) * 100, 100);
        const tier = pct >= 80 ? 'red' : pct >= 50 ? 'yellow' : 'green';
        return (
          <div className="budget-indicator">
            <span className={`budget-indicator__label budget-indicator__label--${tier}`}>
              ${spent.toFixed(2)} / ${p.maxBudget.toFixed(2)}
            </span>
            <div className="budget-bar">
              <div
                className={`budget-bar__fill budget-bar__fill--${tier}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'keyCount',
      header: 'Keys',
      render: (p) => p.keyCount?.toString() ?? '--',
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Projects</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add Project
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchProjects}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && projects.length === 0 ? (
        <div className="view__empty">Loading projects...</div>
      ) : (
        <DataTable<Project>
          columns={columns}
          data={projects}
          keyExtractor={(p) => p.projectId}
          onRowClick={(p) => navigate(`/admin/projects/${p.projectId}`)}
          emptyMessage="No projects found. Click 'Add Project' to create one."
        />
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Project"
        wide
      >
        <ProjectForm
          onSubmit={handleCreate}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>
    </div>
  );
}
